import {
  isAccessAllowed,
  isAccessDenied,
  type AccessPrincipal,
  type AccessTarget,
  type PermissionCode,
} from '@snaphire/contracts';
import type { Row } from './api/client';

export const employeeStatusLabels: Record<string, string> = {
  JOINING: 'Ожидает выхода',
  PROBATION: 'На испытательном сроке',
  ACTIVE: 'Активен',
  ON_LEAVE: 'В отпуске',
  INACTIVE: 'Уволен',
  ARCHIVED: 'Архив',
};
export function employeeStatusLabel(status: string, includeProcess = false) {
  return status === 'OFFBOARDING'
    ? includeProcess
      ? 'Активен (оформляется увольнение)'
      : 'Активен'
    : employeeStatusLabels[status];
}
export function employeeStatusMatches(status: string, bucket: string) {
  return (
    bucket === 'all' ||
    (bucket === 'ACTIVE' ? ['ACTIVE', 'OFFBOARDING'].includes(status) : status === bucket)
  );
}
export const projectStatusLabels: Record<string, string> = {
  ACTIVE: 'Активный',
  COMPLETED: 'Завершён',
  ARCHIVED: 'Архив',
};

export function employeeAccessTarget(
  employee: Row,
  account?: { id: string; employeeId?: string | null } | null,
): Partial<AccessTarget> {
  return {
    entityType: 'Employee',
    entityId: employee.id,
    organizationId: String(employee.organizationId || ''),
    departmentId: String(employee.departmentId || ''),
    teamId: String(employee.primaryTeamId || ''),
    ownerAccountId: employee.accountId ? String(employee.accountId) : undefined,
    lineManagerAccountIds:
      account?.employeeId && employee.lineManagerId === account.employeeId ? [account.id] : [],
  };
}

export function employeeAccessTargets(
  employee: Row,
  account?: { id: string; employeeId?: string | null } | null,
): Partial<AccessTarget>[] {
  const base = employeeAccessTarget(employee, account);
  const projects = [
    ...new Set(
      [
        employee.primaryTeamId,
        ...(Array.isArray(employee.additionalTeamIds) ? employee.additionalTeamIds : []),
      ].filter((id): id is string => typeof id === 'string' && Boolean(id)),
    ),
  ];
  return projects.length ? projects.map((teamId) => ({ ...base, teamId })) : [base];
}

export function canAccessTargets(
  principal: AccessPrincipal,
  permission: PermissionCode,
  targets: readonly Partial<AccessTarget>[],
) {
  const resolved = targets.map((target) => ({ workspaceId: principal.workspaceId, ...target }));
  return (
    !resolved.some((target) => isAccessDenied(principal, permission, target)) &&
    resolved.some((target) => isAccessAllowed(principal, permission, target))
  );
}

export function organizationAccessTarget(key: string, row: Row): Partial<AccessTarget> | undefined {
  const entityType =
    key === 'departments'
      ? 'Department'
      : key === 'teams'
        ? 'Team'
        : key === 'positions'
          ? 'Position'
          : undefined;
  return entityType
    ? {
        entityType,
        entityId: row.id,
        organizationId: String(row.organizationId || ''),
        departmentId: key === 'departments' ? row.id : String(row.departmentId || ''),
        teamId: key === 'teams' ? row.id : undefined,
      }
    : undefined;
}
